import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import { DesktopDialogOverlay } from "./DesktopDialogOverlay";
import { cn } from "../../lib/utils";

/**
 * The desktop dialog card (Figma DS 5.0 "DesktopDialog", drawn from the room
 * welcome, Desktop 5588:229733): 480px wide, 28px side margins, a bare close
 * cross, the title beside it (`inline`) or under its row (`stacked`, where a
 * leading mark such as a room's logo can sit beside the cross), a line under
 * the title (a badge), then the body, then the footer, all 16px apart. The body
 * scrolls between the title and the footer, which stay in view.
 *
 * `modal` centres it over a backdrop that holds the keyboard
 * (`DesktopDialogOverlay`). `corner` sets it in the bottom-right corner of the
 * window, 384px wide, with no backdrop: the page behind stays in use (the
 * "How did you hear about us?" card on the JuisciGPT home, Figma Desktop
 * 5736:129424). It rises in 2s after it mounts, once the page has settled.
 *
 * Whether it is open is the caller's: it renders this or it does not.
 */
export function DesktopDialog({
  presentation,
  title,
  header,
  subtitle,
  onClose,
  footer,
  children,
}: {
  presentation: "modal" | "corner";
  /** The heading, and the dialog's accessible name. */
  title: string;
  /**
   * `inline`: the title on the close cross's row (a source). `stacked`: the
   * cross on a row of its own, beside a leading mark when there is one (a
   * room's logo), and the title under it (the room welcome, About the room).
   */
  header:
    | { layout: "inline" }
    | { layout: "stacked"; leading: React.ReactElement | null };
  /** The line 8px under the title (a "Supported by" badge). Null when it has none. */
  subtitle: React.ReactElement | null;
  onClose: () => void;
  /** The actions under the body (a contact line, a button). Null when it has none. */
  footer: React.ReactElement | null;
  children: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const heading = (
    <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
      <h2 className="m-0 font-dm-sans text-desktop-heading font-semibold text-ds-black">
        {title}
      </h2>
      {subtitle}
    </div>
  );
  const close = (
    <button
      type="button"
      aria-label={t("Close")}
      onClick={onClose}
      className="flex size-5 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-ds-slate-600 hover:text-ds-slate-900"
    >
      <IconX className="size-5" stroke={1.5} />
    </button>
  );
  const content = (
    <>
      {header.layout === "inline" ? (
        <div className="flex shrink-0 items-start gap-4 px-7">
          {heading}
          {close}
        </div>
      ) : (
        <div className="flex shrink-0 flex-col gap-4 px-7">
          <div
            className={cn(
              "flex items-start gap-4",
              header.leading === null ? "justify-end" : "justify-between"
            )}
          >
            {header.leading}
            {close}
          </div>
          {heading}
        </div>
      )}
      <div className="flex min-h-0 flex-col items-start gap-4 overflow-y-auto px-7">
        {children}
      </div>
      {footer !== null && (
        <div className="flex shrink-0 flex-col gap-4 px-7">{footer}</div>
      )}
    </>
  );
  const card =
    "flex max-h-full flex-col gap-4 rounded-2xl bg-ds-white py-6 shadow-desktop-modal";
  switch (presentation) {
    case "modal":
      return (
        <DesktopDialogOverlay
          label={title}
          onClose={onClose}
          cardClassName={cn(card, "w-full max-w-120")}
        >
          {content}
        </DesktopDialogOverlay>
      );
    case "corner":
      return (
        <section
          role="dialog"
          aria-label={title}
          className={cn(
            card,
            "fixed right-6 bottom-4 z-50 w-96 animate-corner-dialog-in"
          )}
        >
          {content}
        </section>
      );
    default:
      throw new Error(presentation satisfies never);
  }
}
